import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { BillingProfileResponse } from "../../types/billing";

interface Props {
  billingProfile: BillingProfileResponse;
  companyId: string;
  onClose: () => void;
  onRenamed: (name: string) => Promise<void> | void;
}

export default function RenameBillingProfileDialog({ billingProfile, companyId, onClose, onRenamed }: Props) {
  const { patch } = useApi();
  const [name, setName] = useState(billingProfile.billingProfileName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const unchanged = trimmed === billingProfile.billingProfileName;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === "backdropClick") return;
    handleClose();
  };

  const handleSubmit = async () => {
    if (!trimmed || unchanged || saving) return;
    setSaving(true);
    setError(null);
    try {
      await patch(`/api/companies/${companyId}/billing-profiles/${billingProfile.billingProfileId}`, { name: trimmed });
      await onRenamed(trimmed);
    } catch (err: any) {
      setError(err.message || "Failed to rename billing profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <DialogTitle>Rename billing profile</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !trimmed || unchanged}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
